import type { Nullable } from '@gov-nx/core/types/helpers.types';
import { prop } from '@gov-nx/core/types/helpers.types';
import type { PageCode } from '@gov-nx/module/page/definitions/codes';
import type { ServiceCode } from '@gov-nx/module/service/definitions/codes';
import { usePersistedStore } from '../constants';
import type { CmsState } from './store.types';

const initialState = {
	services: {},
	pages: {},
	externalServices: {},
};

const useStore = <T>(selector: (state: CmsState) => T) =>
	usePersistedStore(
		(set, get) => ({
			...initialState,

			saveCmsService: (code, data) => {
				const prevServices = get().services;
				set({
					services: { ...prevServices, [code]: { ...data } },
				});
			},
			saveCmsExternalService: (id, data) => {
				const prevExternalServices = get().externalServices;
				set({
					externalServices: { ...prevExternalServices, [id]: { ...data } },
				});
			},
			saveCmsPage: (code, data) => {
				const prevPages = get().pages;
				set({
					pages: { ...prevPages, [code]: { ...data } },
				});
			},
			resetCms: () => set({ ...initialState }),
		}),
		'cms',
		selector
	);

export const useCmsStore = <T extends keyof CmsState>(
	property: T
): CmsState[T] => useStore(prop(property));

export const useCmsStorePage = (code: PageCode) => useCmsStore('pages')[code];

export const useCmsStoreService = (code: ServiceCode) =>
	useCmsStore('services')[code];

export const useCmsStoreExternalService = (id: Nullable<number>) => {
	const externalServices = useCmsStore('externalServices');
	return id ? externalServices[id] : null;
};
